import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NOTIFY_CHANNEL_NAMES, SMTP_PORTS, emailConfigSchema, webhookUrlProblem, type NotifyChannel } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import type { Plugin } from '@/lib/types';
import { Button, Dialog, Field, Input, Select, Textarea, Toggle, useToast } from '@/components/ui';

/** Where to find the incoming-webhook URL in each chat app. */
const HOW_TO: Record<Exclude<NotifyChannel, 'email'>, string> = {
  slack: 'In Slack, add the “Incoming Webhooks” app to the channel and copy its Webhook URL (https://hooks.slack.com/services/…).',
  teams:
    'In the Teams channel open ••• → Workflows → “Post to a channel when a webhook request is received”, finish the steps and copy the URL it shows.',
  'zoho-cliq':
    'In Cliq open Bots & Tools → Webhook Tokens and create a token, then use https://cliq.zoho.com/api/v2/channelsbyname/<channel unique name>/message?zapikey=<token> (your Zoho domain, e.g. cliq.zoho.eu).',
};

const str = (v: unknown, fallback = '') => (typeof v === 'string' ? v : fallback);

/**
 * Settings of a notification plugin: the chat webhook URL, or email recipients and SMTP server.
 * Secrets are write-only: a saved one is never shown, and leaving the field empty keeps it.
 */
export function NotifyChannelEditor({ plugin, channel, orgId, projectId, onClose }: { plugin: Plugin; channel: NotifyChannel; orgId: string; projectId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  // Editing a project override starts from the organization's settings but saves its own.
  const ownSetting = plugin.id !== '' && plugin.projectId === projectId;
  const savedSecret = ownSetting && !!plugin.hasSecret;
  const cfg = plugin.config ?? {};
  const [secret, setSecret] = useState('');
  const [recipients, setRecipients] = useState(Array.isArray(cfg.recipients) ? (cfg.recipients as string[]).join(', ') : '');
  const [host, setHost] = useState(str(cfg.host));
  const [port, setPort] = useState(typeof cfg.port === 'number' ? cfg.port : 587);
  const [secure, setSecure] = useState(cfg.secure === true);
  const [username, setUsername] = useState(str(cfg.username));
  const [from, setFrom] = useState(str(cfg.from));
  const [problem, setProblem] = useState<string | null>(null);
  const name = NOTIFY_CHANNEL_NAMES[channel];

  const emailConfig = () => ({
    recipients: recipients
      .split(/[\s,;]+/)
      .map((r) => r.trim())
      .filter(Boolean),
    host: host.trim(),
    port,
    secure,
    ...(username.trim() ? { username: username.trim() } : {}),
    from: from.trim(),
  });

  const save = useMutation({
    mutationFn: () =>
      api.upsertPlugin(orgId, channel, {
        name,
        enabled: true,
        config: channel === 'email' ? emailConfig() : {},
        projectId,
        ...(secret.trim() ? { secret: secret.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success(`${name} connected - use “Send test” to check it`);
      qc.invalidateQueries({ queryKey: ['plugins', orgId] });
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    let p: string | null = null;
    if (channel === 'email') {
      const r = emailConfigSchema.safeParse(emailConfig());
      p = r.success ? null : (r.error.issues[0]?.message ?? 'Check the settings');
    } else if (secret.trim()) {
      p = webhookUrlProblem(channel, secret.trim());
    } else if (!savedSecret) {
      p = 'Paste the webhook URL.';
    }
    setProblem(p);
    if (!p) save.mutate();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Connect ${name}`}
      description="Schedules and webhooks notify here when a run fails (and when it passes again). Each schedule or webhook can change that."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="notify-form" loading={save.isPending}>
            Save and turn on
          </Button>
        </>
      }
    >
      <form id="notify-form" onSubmit={submit} className="space-y-4">
        {channel !== 'email' ? (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">{HOW_TO[channel]}</p>
            <Field label="Webhook URL" hint={savedSecret ? 'A URL is saved (it is never shown). Leave empty to keep it, or paste a new one.' : 'Stored encrypted; anyone with it can post to the channel.'}>
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="off"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder={savedSecret ? '•••••••• saved' : 'https://…'}
                  className="font-mono"
                />
              )}
            </Field>
          </>
        ) : (
          <>
            <Field label="Send to" hint="Email addresses, separated by commas (up to 20).">
              {(id) => <Textarea id={id} value={recipients} onChange={(e) => setRecipients(e.target.value)} rows={2} placeholder="qa-team@example.com, lead@example.com" />}
            </Field>
            <div className="grid gap-4 sm:grid-cols-[1fr_9rem]">
              <Field label="SMTP server">{(id) => <Input id={id} value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.example.com" />}</Field>
              <Field label="Port">
                {(id) => (
                  <Select
                    id={id}
                    value={String(port)}
                    onChange={(e) => {
                      const p = Number(e.target.value);
                      setPort(p);
                      setSecure(p === 465);
                    }}
                    options={SMTP_PORTS.map((p) => ({ value: String(p), label: String(p) }))}
                  />
                )}
              </Field>
            </div>
            <div className="flex items-start gap-3">
              <Toggle checked={secure} onChange={setSecure} label="Use TLS from the start" />
              <div className="text-sm">
                <div className="font-medium">Use TLS from the start</div>
                <div className="text-xs text-slate-500">On for port 465. Off: the connection is upgraded with STARTTLS when the server offers it (port 587).</div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="User name (optional)">{(id) => <Input id={id} value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />}</Field>
              <Field label="Password" hint={savedSecret ? 'Saved (never shown). Leave empty to keep it.' : 'Stored encrypted.'}>
                {(id) => <Input id={id} type="password" autoComplete="new-password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={savedSecret ? '•••••••• saved' : ''} />}
              </Field>
            </div>
            <Field label="From address">{(id) => <Input id={id} value={from} onChange={(e) => setFrom(e.target.value)} placeholder="1qa@example.com" />}</Field>
          </>
        )}
        {problem && (
          <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">
            {problem}
          </p>
        )}
      </form>
    </Dialog>
  );
}
